/**
 * Super Admin Layout - ChatDesignConcept 테마 기반
 * 
 * 좌측: Navigation Sidebar
 * 중앙: Main Content Area
 */

import { t, Trans } from '@lingui/macro';
import { Building2, CreditCard, FileText, LayoutDashboard, Settings, Users, ChevronRight, Shield, LogOut, Layers, Tag, MessageCircle, DollarSign, Receipt } from 'lucide-react';
import React, { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { SuperAdmin } from '../../services/SuperAdmin';
import { Fire } from '../../services/FireClient';
import { IconChevronDown } from '../components/FigmaIcons';

interface NavItem {
    path: string;
    label: string;
    icon: React.ElementType;
    children?: NavItem[];
}

const NAV_ITEMS: NavItem[] = [
    { path: '/super-admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/super-admin/workspaces', label: 'Workspaces', icon: Building2 },
    { path: '/super-admin/inquiries', label: 'Inquiries', icon: MessageCircle },
    {
        path: '/super-admin/revenue',
        label: 'Revenue',
        icon: DollarSign,
        children: [
            { path: '/super-admin/revenue/subscriptions', label: 'Subscriptions', icon: CreditCard },
            { path: '/super-admin/revenue/invoices', label: 'Invoices', icon: Receipt },
        ]
    },
    { path: '/super-admin/tiers', label: 'Tiers', icon: Layers },
    { path: '/super-admin/promotions', label: 'Promotions', icon: Tag },
    {
        path: '/super-admin/settings',
        label: 'Settings',
        icon: Settings,
        children: [
            { path: '/super-admin/settings/issuer', label: 'Issuer Info', icon: Building2 },
            { path: '/super-admin/settings/security', label: 'Security', icon: Shield },
        ]
    },
];

const SuperAdminLayout: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [expandedGroups, setExpandedGroups] = useState<string[]>(['/super-admin/revenue', '/super-admin/settings']);

    const toggleGroup = (path: string) => {
        setExpandedGroups(prev =>
            prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]
        );
    };

    const handleLogout = async () => {
        // Super Admin 상태 초기화
        SuperAdmin.clear();
        // Firebase Auth 로그아웃
        await Fire.auth.logoutUser();
        // 로그인 페이지로 이동
        navigate('/super-login', { replace: true });
    };

    const styles = {
        sidebar: 'bg-slate-50/50 backdrop-blur-xl border-r border-slate-200/60',
        navItem: (active: boolean) => cn(
            'group flex items-center gap-2.5 px-3 py-2 mb-1 cursor-pointer transition-all duration-200 rounded-lg mx-2',
            active
                ? 'bg-indigo-50/80 text-indigo-700 font-semibold'
                : 'text-slate-600 font-medium hover:bg-slate-100/50 hover:text-slate-900'
        ),
        header: 'bg-white/80 backdrop-blur-md border-b border-slate-100/50 sticky top-0 z-10 shadow-sm shadow-slate-200/20',
    };

    const renderNavItem = (item: NavItem, depth: number = 0) => {
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedGroups.includes(item.path);
        const isActive = location.pathname === item.path ||
            (item.path !== '/super-admin' && location.pathname.startsWith(item.path));
        const isChildActive = item.children?.some(child => location.pathname.startsWith(child.path));

        if (hasChildren) {
            return (
                <div key={item.path}>
                    <div
                        className={cn(
                            styles.navItem(isChildActive || false),
                            'justify-between'
                        )}
                        onClick={() => toggleGroup(item.path)}
                    >
                        <div className="flex items-center gap-2.5">
                            <item.icon className={cn(
                                'w-4 h-4 transition-colors',
                                isChildActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'
                            )} />
                            <span className="text-label">{item.label}</span>
                        </div>
                        <IconChevronDown className={cn(
                            'w-3.5 h-3.5 transition-transform text-slate-400',
                            isExpanded && 'rotate-180'
                        )} />
                    </div>
                    {isExpanded && (
                        <div className="ml-4">
                            {item.children!.map(child => renderNavItem(child, depth + 1))}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <NavLink
                key={item.path}
                to={item.path}
                className={styles.navItem(isActive)}
            >
                <item.icon className={cn(
                    'w-4 h-4 transition-colors',
                    isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'
                )} />
                <span className="text-label">{item.label}</span>
                {isActive && (
                    <ChevronRight className="w-3.5 h-3.5 ml-auto text-indigo-400" />
                )}
            </NavLink>
        );
    };

    return (
        <div className="flex h-screen w-full font-sans text-slate-900 overflow-hidden bg-white">
            {/* Sidebar */}
            <div className={cn('w-[240px] flex flex-col shrink-0', styles.sidebar)}>
                {/* Logo / Header */}
                <div className="h-16 flex items-center px-5 border-b border-slate-200/60">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                            <Shield className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-slate-800 text-sm">Super Admin</span>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 py-4 overflow-y-auto">
                    <div className="px-4 mb-2">
                        <span className="text-meta-sm font-bold text-slate-400 uppercase tracking-widest">
                            <Trans>Management</Trans>
                        </span>
                    </div>
                    {NAV_ITEMS.map((item) => renderNavItem(item))}
                </nav>

                {/* Footer with Logout */}
                <div className="p-4 border-t border-slate-200/60 space-y-3">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Users className="w-3.5 h-3.5" />
                        <span>{SuperAdmin.email || 'Super Admin'}</span>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                        <LogOut className="w-4 h-4" />
                        <span><Trans>Logout</Trans></span>
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                <Outlet />
            </div>
        </div>
    );
};

export default SuperAdminLayout;
